import { useDeliveryApi } from 'infrastructure/api';
import { cloneDeep } from 'lodash';
import { ResourceId } from 'framework/crud';
import {
  ExpectedStructureType,
  MapedExpectedStructure,
  MappedTeamMember,
  ProjectTeamFromData,
  ProjectTeamMemberAlias,
  ProjectTeamType,
} from './types';
import projectTeamValidationSchema from './projectTeamValidationSchema';
import { formatDecimal } from 'framework/utils';
import { PROJECT_TEAM_TYPE } from './constants';
import { FormFieldsData } from 'framework/form';
import { projectCriticalPeriodsValidation } from './projectCriticalPeriodsValidationSchema';
import { PlannedLeaveBatchItem, PlannedLeaveFormRow } from './projectPlannedLeaves/types';
import { BillingType } from '../billingTypes';

export const mapExpectedStructureForRequest = (
  expectedStructure: ExpectedStructureType[] | MapedExpectedStructure[],
) => {
  return expectedStructure.map(item => {
    return {
      id: item.id,
      positionId: item.positionId,
      technologyId: item.technologyId,
      requiredRole: item.requiredRole,
      competencyLevel: item.competencyLevel,
      requiredFte: item.requiredFte,
      description: item.description,
    };
  });
};

export const calculateEnhancedTeamStructure = (team?: ProjectTeamType, fte?: string, billingType?: string) => {
  let teamType = team?.type;
  if (billingType) {
    teamType = (
      billingType === BillingType.MILESTONE ? PROJECT_TEAM_TYPE.SCOPE_BASED : PROJECT_TEAM_TYPE.TEAM_BASED
    ) as keyof typeof PROJECT_TEAM_TYPE;
  }

  if (!team?.id) {
    return {
      team: {
        ...(team || {}),
        type: teamType,
        expectedStructure: [] as MapedExpectedStructure[],
      },
      isNeedCreateTeam: true,
      allFte: 0,
      assignedFte: 0,
    };
  }

  const expectedStructure = team?.expectedStructure || [];
  const members = team?.members || [];

  // Map expectedStructure
  const enhancedExpectedStructure: MapedExpectedStructure[] = expectedStructure.map(slot => {
    // filter members
    const slotMembers = members.filter(member => member.slotId === slot.id);

    // Add issues
    const membersWithIssues = slotMembers.map(member => {
      const issues = [];

      // Issues
      if (slot.competencyLevel && member.competencyLevel !== slot.competencyLevel) {
        issues.push('Level mismatch');
      }
      if (slot.positionId && member.positionId !== slot.positionId) {
        issues.push('Position mismatch');
      }

      return {
        ...member,
        issues,
      };
    }) as MappedTeamMember[];

    // Sum fte
    const slotAssignedFte = slotMembers
      .filter(member => !member.isBench && !member.isBackup)
      .reduce((sum, member) => sum + (Number(member.fte) || 0), 0);

    // Slot Issues
    const issues = [];
    if (slotAssignedFte < slot.requiredFte) {
      issues.push(`${formatDecimal(Number(slot.requiredFte) - slotAssignedFte)} FTE understaffed`);
    }
    if (slotAssignedFte > slot.requiredFte) {
      issues.push(`${formatDecimal(slotAssignedFte - Number(slot.requiredFte))} FTE overstaffed`);
    }

    return {
      ...slot,
      // Only in front
      members: membersWithIssues,
      issues,
      assignedFte: slotAssignedFte,
    };
  });

  // General FTE
  const allFte = fte ? parseFloat(fte) : 0;
  const assignedFte = members
    .filter(member => !member.isBench && !member.isBackup)
    .reduce((sumFte, member) => sumFte + (Number(member.fte) ?? 0), 0);

  return {
    allFte,
    assignedFte,
    isNeedCreateTeam: false,
    team: {
      ...team,
      type: teamType,
      expectedStructure: enhancedExpectedStructure,
    },
  };
};

const useProjectTeamService = () => {
  const { sendGetRequest, sendPostRequest, sendPutRequest, sendDeleteRequest } = useDeliveryApi();

  const getProjectTeamByProjectId = async (
    projectId: ResourceId,
  ): Promise<ProjectTeamType | { isNeedCreateTeam: boolean; expectedStructure: [] }> => {
    try {
      const response = await sendGetRequest(`/projects/${projectId}/team`);
      return response.json();
    } catch (error: unknown) {
      // Team is not created
      if ((error as { status?: number })?.status === 404) {
        return {
          isNeedCreateTeam: true,
          expectedStructure: [],
        };
      }
      throw error;
    }
  };

  const getProjectTeamAliases = async (projectId: ResourceId): Promise<ProjectTeamMemberAlias[]> => {
    const response = await sendGetRequest(`/projects/${projectId}/team/aliases`);
    return response.json();
  };

  const createProjectTeam = async (teamData: ProjectTeamFromData): Promise<ProjectTeamType> => {
    try {
      const { projectId, ...formData } = cloneDeep(teamData);
      const response = await sendPostRequest(`/projects/${teamData.projectId}/team`, {
        location: formData.location,
        type: formData.type,
        expectedStructure: mapExpectedStructureForRequest(formData.expectedStructure),
      });
      return response.json();
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error(error);
      return undefined as unknown as ProjectTeamType;
    }
  };

  const updateProjectTeam = async (teamData: ProjectTeamFromData): Promise<ProjectTeamType> => {
    try {
      const { projectId, ...formData } = cloneDeep(teamData);
      const response = await sendPutRequest(`/projects/team/${teamData.id}`, {
        id: formData.id,
        location: formData.location,
        type: formData.type,
        expectedStructure: mapExpectedStructureForRequest(formData.expectedStructure),
      });
      return response.json();
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error(error);
      return undefined as unknown as ProjectTeamType;
    }
  };

  // critical periods

  const createCriticalPeriod = async (projectId: ResourceId, data: FormFieldsData) => {
    const response = await sendPostRequest(`/projects/${projectId}/critical-periods`, data);
    return response.json();
  };

  const updateCriticalPeriod = async (criticalPeriodId: ResourceId, data: FormFieldsData) => {
    const response = await sendPutRequest(`/projects/critical-periods/${criticalPeriodId}`, data);
    return response.json();
  };

  const deleteCriticalPeriod = async (criticalPeriodId: ResourceId) => {
    return await sendDeleteRequest(`/projects/critical-periods/${criticalPeriodId}`);
  };

  const getProjectTeamValidationSchema = () => Promise.resolve(projectTeamValidationSchema);
  const getCriticalPeriodsValidationSchema = () => Promise.resolve(projectCriticalPeriodsValidation);

  const teamPlannedLeavesBatch = async (
    projectId: ResourceId,
    batch: PlannedLeaveBatchItem[],
  ): Promise<PlannedLeaveFormRow[]> => {
    const response = await sendPostRequest(`/projects/${projectId}/team/planned-leaves/batch`, {
      projectId,
      batch,
    });
    return response.json();
  };

  return {
    createProjectTeam,
    updateProjectTeam,
    getProjectTeamValidationSchema,
    getProjectTeamByProjectId,
    getProjectTeamAliases,
    createCriticalPeriod,
    updateCriticalPeriod,
    deleteCriticalPeriod,
    getCriticalPeriodsValidationSchema,
    teamPlannedLeavesBatch,
  };
};

export default useProjectTeamService;
